import { isEmpty } from "lodash"
import PropTypes from "prop-types"
import React, { Component } from "react"
import { connect } from "react-redux"
import Webcam from "react-webcam"
import { bindActionCreators } from "redux"
import { t } from "../../i18next"
import DiscardPictureIcon from "../../images/icons/discard_picture-108.png"
import DiscardDrawingIcon from "../../images/icons/discard_drawing-108.png"
import ArrowLeftIcon from "../../images/icons/arrow_left-68.png"
import ArrowRightIcon from "../../images/icons/arrow_right-68.png"
import CameraPlusIcon from "../../images/icons/camera_plus-108.png"
import CameraSwitchIcon from "../../images/icons/camera_switch-108.png"
import TextInputIcon from "../../images/icons/text_input-108.png"
import { PageNotFound } from "../common"
import * as actions from "./redux/actions"
import { DamageCaseIdShape, FeatureStateShape } from "./redux/shapes"
import PictureCanvas from "./PictureCanvas"
import * as Screens from "./screens"
import { DamageCaseNavigation, DamageCasePage, DamageCasePageHeader } from "."

export const MAX_PICTURES = 6
export const PICTURE_WIDTH = 640
export const PICTURE_HEIGHT = 480

export class PicturesPage extends Component {
  static propTypes = {
    damageCase: FeatureStateShape.isRequired,
    actions: PropTypes.object.isRequired,
    caseId: DamageCaseIdShape.isRequired,
  }

  constructor() {
    super()

    this.state = {
      cameraError: null,
      constraints: { facingMode: "environment" },
    }

    this.pictureCanvas = React.createRef()
    this.webcam = React.createRef()
  }

  componentDidMount() {
    const { caseId, screen, focusedPictureNr } = this.props
    const { unsentCases } = this.props.damageCase
    const currentCase = unsentCases[caseId]
    const pictures = currentCase.pictures

    if (pictures.length > 0) {
      if (screen !== Screens.PICTURES_SHOW || focusedPictureNr !== pictures.length) {
        this.showPicture(pictures.length)()
      }
    } else {
      if (screen !== Screens.PICTURES_CAMERA) {
        this.showCamera()
      }
    }
  }

  showPicture = focusedPictureNr => () => {
    this.props.actions.showScreen(Screens.PICTURES_SHOW, { focusedPictureNr })
  }

  showCamera = () => {
    this.props.actions.showScreen(Screens.PICTURES_CAMERA)
  }

  showTextInput = () => {
    this.props.actions.showScreen(Screens.PICTURES_TEXT)
  }

  cameraError = err => {
    this.setState({ cameraError: err.message })
  }

  capture = () => {
    const base64 = this.webcam.current.getScreenshot()
    if (base64) {
      this.addPicture(base64)
      this.setState({ cameraError: null })
    } else {
      this.setState({ cameraError: t("damageCase.cameraError") })
    }
  }

  switchCamera = () => {
    if (this.state.constraints.facingMode === "user") {
      this.setState({ constraints: { facingMode: "environment" } })
    } else {
      this.setState({ constraints: { facingMode: "user" } })
    }
  }

  addFromFileInput = ev => {
    for (let idx = 0, file; (file = ev.target.files[idx]); idx++) {
      this.readPictureFromFile(file).then(this.addPicture)
    }
  }

  readPictureFromFile = file => {
    return new Promise((resolve, reject) => {
      var reader = new FileReader()
      const img = document.createElement("img")

      reader.onload = () => {
        img.src = reader.result
      }

      img.onload = () => {
        const base64 = this.resizeImage(img, PICTURE_WIDTH, PICTURE_HEIGHT)
        resolve(base64)
      }

      reader.readAsDataURL(file)
    })
  }

  resizeImage = (img, maxWidth, maxHeight) => {
    let width = img.width
    let height = img.height
    if (width > height) {
      if (width > maxWidth) {
        height *= maxWidth / width
        width = maxWidth
      }
    } else {
      if (height > maxHeight) {
        width *= maxHeight / height
        height = maxHeight
      }
    }

    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")
    ctx.drawImage(img, 0, 0, width, height)

    return canvas.toDataURL("image/jpeg")
  }

  addPicture = base64 => {
    const timestamp = Math.floor(Date.now() / 1000)
    this.props.actions.addPicture(this.props.caseId, base64, timestamp)
  }

  discardPicture = nr => ev => {
    ev.stopPropagation()
    this.props.actions.discardPicture(this.props.caseId, nr)
  }

  discardDrawing = () => {
    this.pictureCanvas.current && this.pictureCanvas.current.discard()
  }

  rememberDrawing = focusedPictureNr => drawingData => {
    this.props.actions.rememberDrawing(this.props.caseId, focusedPictureNr, drawingData)
  }

  rememberText = ev => {
    this.props.actions.rememberAttribute(this.props.caseId, "text", ev.target.value)
  }

  render() {
    const { caseId } = this.props
    const { unsentCases } = this.props.damageCase
    const currentCase = unsentCases[caseId]

    if (currentCase) {
      return this.renderCase(currentCase)
    } else {
      return <PageNotFound />
    }
  }

  renderCase(currentCase) {
    const { caseId } = this.props
    const { screen, focusedPictureNr } = this.props.damageCase
    const { pictures, text } = currentCase
    const focusedPicture = screen === Screens.PICTURES_SHOW && pictures.find((_, idx) => idx === focusedPictureNr - 1)

    const submitCasePending = Boolean(this.props.damageCase.submitCasePending.find(id => id === caseId))
    const submitCaseError = this.props.damageCase.submitCaseError[caseId]
    const dismissSubmitCaseError = () => this.props.actions.dismissSubmitCaseError(caseId)

    return (
      <DamageCasePage
        className="damage-case-pictures-page"
        submitPending={submitCasePending}
        submitError={submitCaseError}
        dismissSubmitCaseError={dismissSubmitCaseError}
      >
        <DamageCasePageHeader caseId={caseId} />
        <div className="app-content">
          <DamageCaseNavigation className="app-content_left" caseId={caseId} currentPage="pictures" />
          <div className="app-content_middle">
            {this.renderPicturesAndControls(pictures, focusedPicture, screen)}
            {this.renderLowerPart(focusedPicture, screen, text)}
          </div>
        </div>
      </DamageCasePage>
    )
  }

  renderPicturesAndControls(pictures, focusedPicture, screen) {
    return (
      <div className="pictures">
        {pictures.map((picture, idx) => (
          <div
            key={`${idx + 1}_${picture.timestamp}`}
            className={`card picture ${focusedPicture === picture ? "focused" : ""}`}
          >
            <PictureCanvas
              pictureBase64={picture.base64}
              drawingData={picture.drawingData}
              drawingEnabled={false}
              width={108}
              onClick={this.showPicture(idx + 1)}
            >
              <div className="info">Bild {String(idx + 1).padStart(2, "0")}</div>
            </PictureCanvas>
          </div>
        ))}

        {pictures.length < MAX_PICTURES && (
          <div
            className={`card new-picture ${screen === Screens.PICTURES_CAMERA ? "focused" : ""}`}
            onClick={screen === Screens.PICTURES_CAMERA ? this.capture : this.showCamera}
          >
            <img className="icon icon-camera_plus" alt="+" src={CameraPlusIcon} />
          </div>
        )}

        <div
          className={`card show-text ${screen === Screens.PICTURES_TEXT ? "focused" : ""}`}
          onClick={this.showTextInput}
        >
          <img className="icon icon-text_input" alt="T" src={TextInputIcon} />
        </div>
      </div>
    )
  }

  renderLowerPart(focusedPicture, screen, text) {
    switch (screen) {
      case Screens.PICTURES_SHOW:
        return focusedPicture && this.renderPictureDetail(focusedPicture, screen)

      case Screens.PICTURES_CAMERA:
        return this.renderCamera(screen)

      case Screens.PICTURES_TEXT:
        return this.renderTextInput(screen, text)

      default:
        return null
    }
  }

  renderPictureDetail(focusedPicture, screen) {
    const { caseId } = this.props
    const { focusedPictureNr, unsentCases } = this.props.damageCase
    const { pictures } = unsentCases[caseId]

    return (
      <div className={`lower-part ${screen}`}>
        <div className="toolbar toolbar-left">
          <button
            className="prev-picture"
            onClick={this.showPicture(focusedPictureNr - 1)}
            disabled={focusedPictureNr === 1}
          >
            <img className="icon icon-arrow_left" alt="<" src={ArrowLeftIcon} />
          </button>
          <button className="discard-picture" onClick={this.discardPicture(focusedPictureNr)}>
            <img className="icon icon-discard_picture" alt="-" src={DiscardPictureIcon} />
          </button>
        </div>

        <PictureCanvas
          ref={this.pictureCanvas}
          className="center"
          pictureBase64={focusedPicture.base64}
          drawingData={focusedPicture.drawingData}
          saveDrawingData={this.rememberDrawing(focusedPictureNr)}
        />

        <div className="toolbar toolbar-right">
          {focusedPictureNr < pictures.length && (
            <button className="next-picture" onClick={this.showPicture(focusedPictureNr + 1)}>
              <img className="icon icon-arrow_right" alt=">" src={ArrowRightIcon} />
            </button>
          )}
          {focusedPictureNr >= pictures.length && (
            <button className="take-picture big" onClick={this.showCamera} disabled={pictures.length >= MAX_PICTURES}>
              <img className="icon icon-camera_plus" alt="+" src={CameraPlusIcon} />
            </button>
          )}
          <button
            className="discard-drawing"
            onClick={this.discardDrawing}
            disabled={isEmpty(focusedPicture.drawingData)}
          >
            <img className="icon icon-discard_drawing" alt="x" src={DiscardDrawingIcon} />
          </button>
        </div>
      </div>
    )
  }

  renderCamera(screen) {
    const { caseId } = this.props
    const { unsentCases } = this.props.damageCase
    const { pictures } = unsentCases[caseId]

    return (
      <div className={`lower-part ${screen}`}>
        <div className="toolbar toolbar-left">
          <button className="prev-picture" onClick={this.showPicture(pictures.length)} disabled={pictures.length < 1}>
            <img className="icon icon-arrow_left" alt="<" src={ArrowLeftIcon} />
          </button>
          <button className="discard-picture" disabled={true}>
            <img className="icon icon-discard_picture" alt="-" src={DiscardPictureIcon} />
          </button>
        </div>

        <div className="center" onClick={this.capture}>
          {this.state.cameraError && (
            <div className="camera-error">
              <p className="message">{this.state.cameraError}</p>
              <p>
                <label className="alternative-input file">
                  {t("damageCase.cameraAlternativeInputFile")}
                  <br />
                  <input type="file" accept="image/*" multiple onChange={this.addFromFileInput} />
                </label>
              </p>
              <p>
                <label className="alternative-input capture">
                  {t("damageCase.cameraAlternativeInputCapture")}
                  <br />
                  <input type="file" accept="image/*" multiple onChange={this.addFromFileInput} capture="environment" />
                </label>
              </p>
            </div>
          )}
          <Webcam
            onUserMediaError={this.cameraError}
            audio={false}
            ref={this.webcam}
            screenshotFormat="image/jpeg"
            screenshotWidth={PICTURE_WIDTH}
            width={PICTURE_WIDTH}
            height={PICTURE_HEIGHT}
            videoConstraints={this.state.constraints}
          />
        </div>

        <div className="toolbar toolbar-right">
          <button className="take-picture" onClick={this.capture} disabled={pictures.length >= MAX_PICTURES}>
            <img className="icon icon-camera_plus" alt="+" src={CameraPlusIcon} />
          </button>
          <button className="switch-camera" onClick={this.switchCamera}>
            <img className="icon icon-camera_switch" alt="S" src={CameraSwitchIcon} />
          </button>
          <button className="discard-drawing" disabled={true}>
            <img className="icon icon-discard_drawing" alt="x" src={DiscardDrawingIcon} />
          </button>
        </div>
      </div>
    )
  }

  renderTextInput(screen, text) {
    return (
      <div className={`lower-part ${screen}`}>
        <textarea onBlur={this.rememberText} defaultValue={text} placeholder={t("damageCase.textInputPlaceholder")} />
      </div>
    )
  }
}

/* istanbul ignore next */
function mapStateToProps(state, ownProps) {
  const { caseId } = ownProps.match.params

  return {
    damageCase: state.damageCase,
    caseId,
  }
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PicturesPage)



// WEBPACK FOOTER //
// ./src/features/damage-case/PicturesPage.js